import { parse } from '@o3co/ts.hocon';
import { KmsDriver } from 'app/constants';

import { getEnvDefault } from './get-env-default';

export interface KmsDefaults {
  kmsValue: string;
  kmsServiceAccount: string;
  kmsAzureVaultSecretName: string;
  overrideImage: string;
  imagePullSecret: string;
}

// Looks for "azure" only in the HOCON fields that actually discriminate the
// driver (type / name / vault-url), so an AWS value that merely mentions azure
// in a key alias doesn't flip the classification. Falls back to a raw substring
// test when the value isn't parseable. A backend-provided driver field would
// make this heuristic unnecessary.
const kmsValueIndicatesAzure = (kmsValue?: string | null): boolean => {
  const trimmed = kmsValue?.trim();
  if (!trimmed) {
    return false;
  }
  try {
    const parsed = (parse(trimmed).toObject() ?? {}) as Record<string, unknown>;
    const config = (parsed.config ?? {}) as Record<string, unknown>;
    return [parsed.type, parsed.name, config['vault-url']].some(
      (field) => typeof field === 'string' && /azure/i.test(field),
    );
  } catch {
    return /azure/i.test(trimmed);
  }
};

// Azure nodes created with a service account (CONFIG_KMS_AZURE_SA) carry no
// vault secret name, so fall back to inspecting the KMS HOCON.
export const resolveKmsDriver = (config?: {
  kmsAzureVaultSecretName?: string | null;
  kmsValue?: string | null;
}): KmsDriver =>
  config?.kmsAzureVaultSecretName || kmsValueIndicatesAzure(config?.kmsValue) ? KmsDriver.AZURE : KmsDriver.AWS;

// When true, the Azure KMS form asks for a service account (same as AWS)
// instead of a vault secret name. `.docker/env.sh` emits booleans unquoted,
// so the runtime value can be a real boolean, not just "true".
export const isKmsAzureServiceAccountEnabled = (): boolean => {
  const runtimeValue =
    typeof window === 'undefined'
      ? undefined
      : (window as Window & { _env_?: Record<string, string | boolean> })._env_?.CONFIG_KMS_AZURE_SA;
  if (runtimeValue !== undefined) return runtimeValue === true || runtimeValue === 'true';
  return import.meta.env.VITE_CONFIG_KMS_AZURE_SA === 'true';
};

// Single source of truth for the create flow's credential shape: AWS always
// takes a service account; Azure takes one only in CONFIG_KMS_AZURE_SA mode
// (otherwise a vault secret name). Form rendering, validation, the submit
// payload, and the summary must all agree on this.
export const kmsCreateUsesServiceAccount = (driver?: KmsDriver): boolean =>
  driver === KmsDriver.AWS || isKmsAzureServiceAccountEnabled();

export const buildKmsDefaults = (driver: KmsDriver): KmsDefaults => {
  if (driver === KmsDriver.AZURE) {
    return {
      kmsValue: getEnvDefault('CONFIG_KMS_DEFAULT_AZURE_VALUE', 'VITE_CONFIG_KMS_DEFAULT_AZURE_VALUE'),
      kmsServiceAccount: '',
      kmsAzureVaultSecretName: '',
      overrideImage: getEnvDefault('CONFIG_KMS_DEFAULT_AZURE_IMAGE', 'VITE_CONFIG_KMS_DEFAULT_AZURE_IMAGE'),
      imagePullSecret: getEnvDefault(
        'CONFIG_KMS_DEFAULT_AZURE_IMAGE_PULL_SECRET',
        'VITE_CONFIG_KMS_DEFAULT_AZURE_IMAGE_PULL_SECRET',
      ),
    };
  }

  return {
    kmsValue: getEnvDefault('CONFIG_KMS_DEFAULT_AWS_VALUE', 'VITE_CONFIG_KMS_DEFAULT_AWS_VALUE'),
    kmsServiceAccount: getEnvDefault(
      'CONFIG_KMS_DEFAULT_AWS_SERVICE_ACCOUNT',
      'VITE_CONFIG_KMS_DEFAULT_AWS_SERVICE_ACCOUNT',
    ),
    kmsAzureVaultSecretName: '',
    overrideImage: '',
    imagePullSecret: '',
  };
};
